import React, { useState, useEffect } from "react";
import { SubmitHandler } from "react-hook-form";
import { Icon } from "@clarkinc/zest-design-system";
import { z } from "zod";
import ErrorAlert from "~/common/Alert/ErrorAlert";
import Form from "~/common/Form";
import SearchResult from "./SearchResult";
import SearchResultNotFound from "./SearchResultNotFound";
import Input from "~/common/Form/Input";
import SupportFAQ from "../Models/SupportFAQ";

type FormFields = {
  searchPhrase: string;
};

export interface FormProps {
  displayHeader: string;
  searchText: string;
}

const validationSchema = z.object({
  searchPhrase: z.string().nonempty("A search phrase is required."),
});

export type Props = FormProps;

export default function Search({ displayHeader, searchText }: Props) {
  const [showResults, setShowResults] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState<SupportFAQ[]>();
  const [errorMessage, setErrorMessage] = useState<string>();

  useEffect(() => {
    if (searchText && searchText.length > 0) {
      getSearchResults(searchText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getSearchResults = async (data: string) => {
    setSearchValue(data);

    try {
      setErrorMessage(undefined);

      const result = await fetch("/api/searchsupportfaq/", {
        method: "POST",
        body: JSON.stringify(data),
      });

      if (result.ok) {
        setShowResults(true);
        const supportFaqs = await result.json<SupportFAQ[]>();
        setSearchResults(supportFaqs);
      } else {
        setErrorMessage(
          "There was an issue submitting your request. Please try again later.",
        );
      }
    } catch {
      setErrorMessage(
        "There was an issue submitting your request. Please try again later.",
      );
    }
  };

  const handleSearch: SubmitHandler<FormFields> = (data) => {
    getSearchResults(data.searchPhrase.trim());
  };

  const buildErrorDisplayMessage = (): React.ReactElement => {
    return <>{errorMessage}</>;
  };

  return (
    <div className="support-section" data-module="supportPage">
      <div className="support-header-container">
        {errorMessage && <ErrorAlert>{buildErrorDisplayMessage()}</ErrorAlert>}
        <div className="support-header-text">
          <h1 className="page-header xs:text-2xl skip-to-main-target">
            {displayHeader}
          </h1>
          <p>
            Need help finding information? Take a look at some of our Frequently
            Asked Questions and helpful links.
          </p>
        </div>
        <div className="support-search input-append whitespace-normal">
          <Form<FormFields>
            validationSchema={validationSchema}
            onSubmit={handleSearch}
            resetOnSubmit
          >
            <label
              htmlFor="searchPhrase"
              className="text-sm mb-1 leading-4 xsl:leading-5"
            >
              Search Our FAQs{" "}
              <span className="font-normal block w-full xsl:inline xsl:w-auto">
                (Example: Do you ship internationally?)
              </span>
            </label>
            <div className="relative">
              <Icon
                name="ri-search-line"
                className="h-5 w-5 fill-gray-600 absolute z-5 pt-3 pl-3"
              />
              <Input
                label=""
                className="pl-9"
                type="text"
                name="searchPhrase"
                aria-label="Search help"
              />
              <button
                className="btn btn-light btn-mini"
                name="btnSearch"
                value="Search"
                type="submit"
                style={{ height: 42 }}
              >
                Search
              </button>
            </div>
          </Form>
        </div>
      </div>
      <div aria-live="polite" aria-atomic key={searchValue}>
        {showResults &&
          searchResults &&
          (searchResults.length > 0 ? (
            <span className="sr-only">{`${searchResults.length} results found for ${searchValue}`}</span>
          ) : (
            <span className="sr-only">{`0 results found for ${searchValue}`}</span>
          ))}
      </div>
      {showResults && searchResults && searchResults.length > 0 && (
        <SearchResult {...{ searchValue, searchResults }} />
      )}
      {showResults && searchResults && searchResults?.length === 0 && (
        <SearchResultNotFound {...{ searchValue }} />
      )}
    </div>
  );
}
